import * as React from 'react';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { RootState } from 'store';
import { logout } from 'store/slices/auth';
import { Map } from 'mapbox-gl';
import { createMenuItems, MenuItemType } from './createMenuItems';
import AvatarMenu from './AvatarMenu';
import { setSearchForHome, showSearchBar } from 'store/slices/searchBar';
import { useMediaQuery, useTheme } from '@mui/material';

interface MainMenuProps {
  mapRef: MutableRefObject<Map | null>;
}

export const BottomMenu = (props: MainMenuProps) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const isLg = useMediaQuery(theme.breakpoints.up('lg'));
    const authd = useAppSelector((state: RootState) => state.auth);
    const home = useAppSelector((state: RootState) => state.home);
    const { userLoginSuccess, jwt } = authd;
    const { latitude, longitude, zoom } = home;
    const latitudeRef = useRef(latitude);
    const longitudeRef = useRef(longitude);
    const zoomRef = useRef(zoom);
    const [isLoggedIn, setIsLoggedIn] = useState(userLoginSuccess && !!jwt);

    const onLogout = () => {
        dispatch(logout());
    };

    useEffect(() => {
        latitudeRef.current = latitude;
        longitudeRef.current = longitude;
        zoomRef.current = zoom;
    }, [latitude, longitude, zoom]);

    const onSearch = () => {
        dispatch(showSearchBar());
    };

    const onHome = () => {
        if (props?.mapRef?.current && latitudeRef.current && longitudeRef.current && zoomRef.current) {
            props.mapRef.current.flyTo({
                zoom: zoomRef?.current,
                center: {
                    lat: latitudeRef?.current,
                    lng: longitudeRef?.current,
                },
            });
            props.mapRef.current.fire('flystart');
        }
    };

    const onSetNewHome = () => {
        dispatch(setSearchForHome());
    };

    const filterByCondition = (arr: MenuItemType[]) => arr.filter((i) => typeof i.condition === 'undefined' || i.condition);

    const { pages: pagesIni, settings: settingsIni } = createMenuItems(
        isLoggedIn,
        onLogout,
        onHome,
        onSetNewHome,
        onSearch
    );
    const [, setPages] = useState(filterByCondition(pagesIni));
    const [settings, setSettings] = useState(filterByCondition(settingsIni));

    useEffect(() => {
        const newLoggedInStatus = userLoginSuccess && !!jwt;
        const { pages: pagesNew, settings: settingsNew } = createMenuItems(
            newLoggedInStatus,
            onLogout,
            onHome,
            onSetNewHome,
            onSearch
        );
        setPages(filterByCondition(pagesNew));
        setSettings(filterByCondition(settingsNew));
        setIsLoggedIn(newLoggedInStatus);
    }, [userLoginSuccess, jwt]);

    return userLoginSuccess && !!jwt && !isLg ? (
        <Box
            component={'footer'}
            sx={{
                position: 'relative',
                height: '0',
                overflow: 'visible',
                display: 'flex',
                backgroundColor: (theme) => theme.palette.primary.main,
                color: (theme) => theme.palette.primary.contrastText,
                p: 0,
            }}
        >
            <AvatarMenu
                settings={settings}
                sx={{
                    position: 'absolute',
                    bottom: '15px',
                    right: '15px',
                    zIndex: (theme) => theme.zIndex.appBar + 2,
                }}
            />
        </Box>
    ) : null;
};
